import React, { useState, useEffect, useCallback } from 'react';
import { collection, query, where, getDocs, doc, setDoc, updateDoc } from 'firebase/firestore';
import { db } from '../firebase';
import { addRequiredSubject, removeRequiredSubject, getSubjectTeachersForClass, getSubjectTeachersForSubject, assignSubjectTeacher, removeSubjectTeacherAssignment } from '../services/SubjectTeacherService';
import './TeacherClassesView.css';
import './TeacherDetailPages.css';

function TeacherClassesView({ teacherData }) {
  const [classes, setClasses] = useState([]);
  const [selectedClass, setSelectedClass] = useState(null);
  const [students, setStudents] = useState([]);
  const [assignments, setAssignments] = useState([]);
  const [attendanceRecords, setAttendanceRecords] = useState([]);
  const [activeTab, setActiveTab] = useState('students');
  const [loading, setLoading] = useState(true);
  const [selectedAssignment, setSelectedAssignment] = useState(null);
  const [showAssignmentDetail, setShowAssignmentDetail] = useState(false);
  const [selectedStudent, setSelectedStudent] = useState(null);
  const [subjectTeachers, setSubjectTeachers] = useState([]);
  const [availableSubjectTeachers, setAvailableSubjectTeachers] = useState({});
  const [showAssignTeacherModal, setShowAssignTeacherModal] = useState(false);
  const [selectedSubjectForAssignment, setSelectedSubjectForAssignment] = useState(null);
  const [selectedTeacherId, setSelectedTeacherId] = useState(null);
  const [loadingTeachers, setLoadingTeachers] = useState(false);
  const [showAddSubjectModal, setShowAddSubjectModal] = useState(false);
  const [newSubject, setNewSubject] = useState('');
  const [showAddStudentModal, setShowAddStudentModal] = useState(false);
  const [editingStudent, setEditingStudent] = useState(null);
  const [studentForm, setStudentForm] = useState({ firstName: '', lastName: '', studentId: '', email: '' });
  const [savingStudent, setSavingStudent] = useState(false);

  const loadClasses = useCallback(async () => {
    if (!teacherData?.uid) {
      setLoading(false);
      return;
    }
    console.log('🔥 TeacherClasses: Loading classes for teacher', teacherData.uid);
    console.log('🔥 TeacherClasses: Teacher data', teacherData);
    try {
      // First, load ALL classes to see what's in the database
      console.log('🔥 TeacherClasses: Loading ALL classes for debugging');
      const allClassesQuery = query(collection(db, 'classes'));
      const allClassesSnapshot = await getDocs(allClassesQuery);
      const allClassesData = allClassesSnapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));
      console.log('🔥 TeacherClasses: ALL classes in database', allClassesData.length, allClassesData);
      console.log('🔥 TeacherClasses: Class teacherIds', allClassesData.map(c => ({ id: c.id, name: c.name, teacherId: c.teacherId })));

      // Load classes for this teacher
      const classesQuery = query(
        collection(db, 'classes'),
        where('teacherId', '==', teacherData.uid)
      );
      const snapshot = await getDocs(classesQuery);
      const classesData = snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));
      console.log('🔥 TeacherClasses: Classes loaded for teacher', classesData.length, classesData);
      setClasses(classesData);
    } catch (error) {
      console.error('🔥 TeacherClasses: Error loading classes:', error);
    } finally {
      setLoading(false);
    }
  }, [teacherData]);

  useEffect(() => {
    loadClasses();
  }, [loadClasses]);

  const loadStudents = async (classId) => {
    console.log('🔥 TeacherClasses: Loading students for class', classId);
    try {
      const studentsQuery = query(
        collection(db, 'classes', classId, 'students'),
        where('isActive', '==', true)
      );
      const snapshot = await getDocs(studentsQuery);
      const studentsData = snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));
      console.log('🔥 TeacherClasses: Students loaded', studentsData.length, studentsData);
      setStudents(studentsData);
    } catch (error) {
      console.error('🔥 TeacherClasses: Error loading students:', error);
    }
  };

  const loadAssignments = async (classId) => {
    console.log('🔥 TeacherClasses: Loading assignments for class', classId);
    try {
      const assignmentsQuery = query(
        collection(db, 'classes', classId, 'assignments')
      );
      const snapshot = await getDocs(assignmentsQuery);
      const assignmentsData = snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));
      console.log('🔥 TeacherClasses: Assignments loaded', assignmentsData.length, assignmentsData);
      setAssignments(assignmentsData);
    } catch (error) {
      console.error('🔥 TeacherClasses: Error loading assignments:', error);
    }
  };

  const loadAttendance = async (classId) => {
    console.log('🔥 TeacherClasses: Loading attendance for class', classId);
    try {
      // Match the ISO format used by iOS: YYYY-MM-DDTHH:mm:ssTZD (with local timezone)
      const startOfDay = new Date();
      startOfDay.setHours(0, 0, 0, 0);
      // Format to match iOS timezone format (e.g., 2026-06-29T00:00:00-04:00)
      const offset = startOfDay.getTimezoneOffset();
      const offsetHours = Math.abs(Math.floor(offset / 60));
      const offsetMinutes = Math.abs(offset % 60);
      const offsetSign = offset <= 0 ? '+' : '-';
      const offsetStr = `${offsetSign}${String(offsetHours).padStart(2, '0')}:${String(offsetMinutes).padStart(2, '0')}`;
      const dateStr = `${startOfDay.getFullYear()}-${String(startOfDay.getMonth() + 1).padStart(2, '0')}-${String(startOfDay.getDate()).padStart(2, '0')}T00:00:00${offsetStr}`;
      console.log('🔥 TeacherClasses: Querying attendance for date', dateStr);

      const attendanceQuery = query(
        collection(db, 'classes', classId, 'attendanceRecords'),
        where('date', '==', dateStr)
      );
      const snapshot = await getDocs(attendanceQuery);
      const attendanceData = snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));
      console.log('🔥 TeacherClasses: Attendance loaded', attendanceData.length, attendanceData);
      setAttendanceRecords(attendanceData);
    } catch (error) {
      console.error('🔥 TeacherClasses: Error loading attendance:', error);
    }
  };

  const handleClassClick = async (cls) => {
    setSelectedClass(cls);
    setSelectedStudent(null);
    setShowAssignmentDetail(false);
    setSelectedAssignment(null);
    setActiveTab('students');
    await loadStudents(cls.id);
    await loadAssignments(cls.id);
    await loadAttendance(cls.id);
    await loadSubjectTeachers(cls.id);
  };

  const handleBack = () => {
    setSelectedClass(null);
    setSelectedStudent(null);
    setShowAssignmentDetail(false);
    setSelectedAssignment(null);
    setStudents([]);
    setAssignments([]);
    setAttendanceRecords([]);
    setSubjectTeachers([]);
    setActiveTab('students');
  };

  const formatDate = (dateValue) => {
    if (!dateValue) return 'N/A';
    if (dateValue.toDate) return dateValue.toDate().toLocaleDateString();
    return new Date(dateValue).toLocaleDateString();
  };

  const getAssignmentIcon = (type) => {
    const icons = {
      homework: '📝',
      quiz: '❓',
      test: '📋',
      project: '🎯',
      exam: '📚',
      classwork: '✏️'
    };
    return icons[type] || '📝';
  };

  const getStudentName = (studentId) => {
    const student = students.find(s => s.id === studentId);
    return student ? student.fullName : studentId;
  };

  const loadSubjectTeachers = async (classId) => {
    const result = await getSubjectTeachersForClass(classId);
    if (result.success) {
      setSubjectTeachers(result.subjectTeachers);
    }
  };

  const loadSubjectTeachersForSubject = async (subject) => {
    setLoadingTeachers(true);
    const result = await getSubjectTeachersForSubject(subject);
    setLoadingTeachers(false);
    if (result.success) {
      setAvailableSubjectTeachers(prev => ({ ...prev, [subject]: result.teachers }));
    }
  };

  const handleAssignSubjectTeacher = async (subject, teacherId) => {
    const result = await assignSubjectTeacher(teacherId, selectedClass.id, subject);
    if (result.success) {
      await loadSubjectTeachers(selectedClass.id);
      setShowAssignTeacherModal(false);
      setSelectedSubjectForAssignment(null);
      setSelectedTeacherId(null);
    } else {
      alert(result.error || 'Failed to assign teacher');
    }
  };

  const handleRemoveSubjectTeacher = async (subject) => {
    const result = await removeSubjectTeacherAssignment(selectedClass.id, subject);
    if (result.success) {
      await loadSubjectTeachers(selectedClass.id);
    }
  };

  const handleAddSubject = async () => {
    if (!newSubject.trim()) return;
    const result = await addRequiredSubject(selectedClass.id, newSubject.trim());
    if (result.success) {
      setNewSubject('');
      setShowAddSubjectModal(false);
      setSelectedClass(prev => ({ ...prev, requiredSubjects: [...(prev.requiredSubjects || []), newSubject.trim()] }));
    } else {
      alert(result.error || 'Failed to add subject');
    }
  };

  const handleRemoveSubject = async (subject) => {
    const result = await removeRequiredSubject(selectedClass.id, subject);
    if (result.success) {
      setSelectedClass(prev => ({ ...prev, requiredSubjects: prev.requiredSubjects.filter(s => s !== subject) }));
    }
  };

  const handleAddStudent = () => {
    setEditingStudent(null);
    setStudentForm({ firstName: '', lastName: '', studentId: '', email: '' });
    setShowAddStudentModal(true);
  };

  const handleEditStudent = (student) => {
    setEditingStudent(student);
    setStudentForm({
      firstName: student.firstName || '',
      lastName: student.lastName || '',
      studentId: student.studentId || '',
      email: student.email || '',
    });
    setShowAddStudentModal(true);
  };

  const handleSaveStudent = async () => {
    if (!studentForm.firstName.trim() || !studentForm.lastName.trim()) {
      alert('First name and last name are required');
      return;
    }
    setSavingStudent(true);
    try {
      const fullName = `${studentForm.firstName.trim()} ${studentForm.lastName.trim()}`;
      const studentId = studentForm.studentId.trim() || crypto.randomUUID();

      if (editingStudent) {
        // Update existing student
        const studentRef = doc(db, 'students', editingStudent.id);
        await updateDoc(studentRef, {
          firstName: studentForm.firstName.trim(),
          lastName: studentForm.lastName.trim(),
          fullName,
          email: studentForm.email.trim(),
        });
        const classStudentRef = doc(db, 'classes', selectedClass.id, 'students', editingStudent.id);
        await updateDoc(classStudentRef, {
          firstName: studentForm.firstName.trim(),
          lastName: studentForm.lastName.trim(),
          fullName,
          email: studentForm.email.trim(),
        });
      } else {
        // Create new student
        const newStudentId = crypto.randomUUID();
        const studentData = {
          firstName: studentForm.firstName.trim(),
          lastName: studentForm.lastName.trim(),
          fullName,
          studentId,
          email: studentForm.email.trim() || null,
          classId: selectedClass.id,
          schoolId: teacherData?.schoolId || null,
          isActive: true,
          createdAt: new Date().toISOString(),
        };
        await setDoc(doc(db, 'students', newStudentId), studentData);
        await setDoc(doc(db, 'classes', selectedClass.id, 'students', newStudentId), studentData);
      }
      setShowAddStudentModal(false);
      await loadStudents(selectedClass.id);
    } catch (error) {
      console.error('Error saving student:', error);
      alert('Failed to save student: ' + error.message);
    } finally {
      setSavingStudent(false);
    }
  };

  if (loading) {
    return (
      <div className="loading-container">
        <div className="spinner"></div>
        <p>Loading...</p>
      </div>
    );
  }

  if (classes.length === 0) {
    return (
      <div className="teacher-classes-view">
        <h2>My Classes</h2>
        <div className="empty-state">
          <div className="empty-icon">🏫</div>
          <h3>No Classes Assigned</h3>
          <p>You don't have any classes assigned yet.</p>
        </div>
      </div>
    );
  }

  if (selectedClass) {
    // Full student detail page (iOS StudentDetailView parity)
    if (selectedStudent) {
      return (
        <TeacherStudentDetailView
          student={selectedStudent}
          cls={selectedClass}
          assignments={assignments}
          onBack={() => setSelectedStudent(null)}
          onEdit={(s) => handleEditStudent(s)}
        />
      );
    }

    // Full assignment detail page (replaces the old cramped modal)
    if (showAssignmentDetail && selectedAssignment) {
      return (
        <div className="detail-page">
          <button className="detail-back-button" onClick={() => setShowAssignmentDetail(false)}>
            ◄ Back to {selectedClass.name}
          </button>
          <div className="detail-hero">
            <div className="detail-avatar">{getAssignmentIcon(selectedAssignment.assignmentType)}</div>
            <div className="detail-hero-info">
              <h2>{selectedAssignment.title}</h2>
              <p className="detail-subtitle">{selectedClass.name}{selectedClass.grade ? ` · ${selectedClass.grade}` : ''}</p>
              <div className="detail-badges">
                <span className="detail-badge">{selectedAssignment.assignmentType || 'Assignment'}</span>
                {selectedAssignment.subjectName && <span className="detail-badge gray">{selectedAssignment.subjectName}</span>}
                <span className={`detail-badge ${selectedAssignment.isActive === false ? 'red' : 'green'}`}>
                  {selectedAssignment.isActive === false ? 'Inactive' : 'Active'}
                </span>
              </div>
            </div>
          </div>
          <div className="detail-grid">
            <div className="detail-card">
              <h3>Assignment Details</h3>
              <div className="detail-field"><span className="field-label">Max Score</span><span className="field-value">{selectedAssignment.maxScore ?? 'N/A'} pts</span></div>
              <div className="detail-field"><span className="field-label">Due Date</span><span className="field-value">{formatDate(selectedAssignment.dueDate)}</span></div>
              <div className="detail-field"><span className="field-label">Class</span><span className="field-value">{selectedClass.name}</span></div>
              <div className="detail-field"><span className="field-label">Subject</span><span className="field-value">{selectedAssignment.subjectName || 'None'}</span></div>
              <div className="detail-field"><span className="field-label">Type</span><span className="field-value">{selectedAssignment.assignmentType || 'N/A'}</span></div>
            </div>
            <div className="detail-card">
              <h3>Description</h3>
              {selectedAssignment.description
                ? <p className="detail-empty" style={{ color: '#374151' }}>{selectedAssignment.description}</p>
                : <p className="detail-empty">No description provided</p>}
            </div>
          </div>
        </div>
      );
    }

    return (
      <div className="teacher-classes-view">
        <div className="class-detail-header">
          <button className="back-button" onClick={handleBack}>
            ← Back to Classes
          </button>
          <div className="class-info">
            <h2>{selectedClass.name}</h2>
            <p>{selectedClass.grade}</p>
          </div>
          <div className="class-stats">
            <div className="stat">
              <span className="stat-value">{students.length}</span>
              <span className="stat-label">Students</span>
            </div>
          </div>
        </div>

        <div className="class-tabs">
          <button
            className={`tab-button ${activeTab === 'students' ? 'active' : ''}`}
            onClick={() => setActiveTab('students')}
          >
            Students
          </button>
          <button
            className={`tab-button ${activeTab === 'assignments' ? 'active' : ''}`}
            onClick={() => setActiveTab('assignments')}
          >
            Assignments
          </button>
          <button
            className={`tab-button ${activeTab === 'attendance' ? 'active' : ''}`}
            onClick={() => setActiveTab('attendance')}
          >
            Attendance
          </button>
          <button
            className={`tab-button ${activeTab === 'subjects' ? 'active' : ''}`}
            onClick={() => setActiveTab('subjects')}
          >
            Subjects
          </button>
        </div>

        {activeTab === 'students' && (
          <div className="students-list">
            <div className="students-header">
              <h3>Students ({students.length})</h3>
              <button className="add-student-btn" onClick={handleAddStudent}>
                + Add Student
              </button>
            </div>
            {students.length === 0 ? (
              <div className="empty-state">
                <p>No students in this class</p>
              </div>
            ) : (
              students.map(student => (
                <div
                  key={student.id}
                  className="student-card"
                  style={{ cursor: 'pointer' }}
                  onClick={() => {
                    console.log('🔥 TeacherClasses: Student tapped', student.fullName, student.id);
                    setSelectedStudent(student);
                  }}
                >
                  <div className="student-info">
                    {student.photoURL ? (
                      <img src={student.photoURL} alt={student.fullName} className="student-photo" />
                    ) : (
                      <div className="student-photo placeholder">👤</div>
                    )}
                    <div>
                      <div className="student-name">{student.fullName}</div>
                      <div className="student-id">ID: {student.studentId}</div>
                    </div>
                  </div>
                  <div className="student-actions">
                    <button className="edit-student-btn" onClick={(e) => { e.stopPropagation(); handleEditStudent(student); }}>
                      Edit
                    </button>
                    <span className={`status-badge ${student.isActive ? 'active' : 'inactive'}`}>
                      {student.isActive ? 'Active' : 'Inactive'}
                    </span>
                  </div>
                </div>
              ))
            )}
          </div>
        )}

        {activeTab === 'assignments' && (
          <div className="assignments-list">
            {assignments.length === 0 ? (
              <div className="empty-state">
                <p>No assignments for this class</p>
              </div>
            ) : (
              assignments.map(assignment => (
                <div
                  key={assignment.id}
                  className="assignment-card"
                  onClick={() => {
                    setSelectedAssignment(assignment);
                    setShowAssignmentDetail(true);
                  }}
                >
                  <div className="assignment-icon">{getAssignmentIcon(assignment.assignmentType)}</div>
                  <div className="assignment-info">
                    <h4>{assignment.title}</h4>
                    <p className="assignment-type">{assignment.assignmentType} {assignment.subjectName ? `• ${assignment.subjectName}` : ''}</p>
                    <p className="assignment-due">Due: {formatDate(assignment.dueDate)}</p>
                  </div>
                  <div className="assignment-score">
                    <span className="score-value">{assignment.maxScore}</span>
                    <span className="score-label">pts</span>
                  </div>
                </div>
              ))
            )}
          </div>
        )}

        {activeTab === 'attendance' && (
          <div className="attendance-list">
            <div className="attendance-summary">
              <div className="summary-item present">
                <span className="count">{attendanceRecords.filter(r => r.status === 'Present').length}</span>
                <span className="label">Present</span>
              </div>
              <div className="summary-item absent">
                <span className="count">{attendanceRecords.filter(r => r.status === 'Absent').length}</span>
                <span className="label">Absent</span>
              </div>
              <div className="summary-item late">
                <span className="count">{attendanceRecords.filter(r => r.status === 'Late').length}</span>
                <span className="label">Late</span>
              </div>
            </div>
            {attendanceRecords.length === 0 ? (
              <div className="empty-state">
                <p>No attendance records for today</p>
              </div>
            ) : (
              attendanceRecords.map(record => (
                <div key={record.id} className="attendance-card">
                  <div className="attendance-info">
                    <div className="student-photo placeholder">👤</div>
                    <div>
                      <h4>{getStudentName(record.studentId)}</h4>
                      <p>ID: {record.studentId}</p>
                    </div>
                  </div>
                  <span className={`attendance-status ${record.status?.toLowerCase()}`}>
                    {record.status}
                  </span>
                </div>
              ))
            )}
          </div>
        )}

        {activeTab === 'subjects' && (
          <div className="subjects-list">
            <div className="subjects-header">
              <h3>Required Subjects</h3>
              <button className="add-subject-button" onClick={() => setShowAddSubjectModal(true)}>
                + Add Subject
              </button>
            </div>
            {(!selectedClass.requiredSubjects || selectedClass.requiredSubjects.length === 0) ? (
              <div className="empty-state">
                <p>No subjects required yet</p>
              </div>
            ) : (
              selectedClass.requiredSubjects.map(subject => {
                const assignedTeacher = subjectTeachers.find(st => st.subject === subject);
                return (
                  <div key={subject} className="subject-card">
                    <div className="subject-info">
                      <h4>{subject}</h4>
                      {assignedTeacher ? (
                        <span className="assigned-badge">Assigned</span>
                      ) : (
                        <span className="unassigned-badge">Unassigned</span>
                      )}
                    </div>
                    <div className="subject-actions">
                      {assignedTeacher ? (
                        <>
                          <span className="teacher-info">Teacher ID: {assignedTeacher.teacherId.substring(0, 8)}</span>
                          <button className="remove-button" onClick={() => handleRemoveSubjectTeacher(subject)}>
                            Remove
                          </button>
                        </>
                      ) : (
                        <button 
                          className="assign-button"
                          onClick={() => {
                            setSelectedSubjectForAssignment(subject);
                            loadSubjectTeachersForSubject(subject);
                            setShowAssignTeacherModal(true);
                          }}
                        >
                          Assign Teacher
                        </button>
                      )}
                      <button className="delete-subject-button" onClick={() => handleRemoveSubject(subject)}>
                        ×
                      </button>
                    </div>
                  </div>
                );
              })
            )}
          </div>
        )}

        {showAssignTeacherModal && selectedSubjectForAssignment && (
          <div className="modal-overlay">
            <div className="modal-content">
              <div className="modal-header">
                <h3>Assign {selectedSubjectForAssignment} Teacher</h3>
                <button className="close-button" onClick={() => setShowAssignTeacherModal(false)}>×</button>
              </div>
              <div className="modal-body">
                {loadingTeachers ? (
                  <div className="loading-container">
                    <div className="spinner"></div>
                    <p>Loading teachers...</p>
                  </div>
                ) : (!availableSubjectTeachers[selectedSubjectForAssignment] || availableSubjectTeachers[selectedSubjectForAssignment].length === 0) ? (
                  <div className="empty-state">
                    <p>No {selectedSubjectForAssignment} teachers available</p>
                    <p className="empty-hint">Contact admin to add a {selectedSubjectForAssignment} teacher</p>
                  </div>
                ) : (
                  <div className="teachers-list">
                    {availableSubjectTeachers[selectedSubjectForAssignment].map(teacher => (
                      <div 
                        key={teacher.id} 
                        className={`teacher-option ${selectedTeacherId === teacher.id ? 'selected' : ''}`}
                        onClick={() => setSelectedTeacherId(teacher.id)}
                      >
                        <div className="teacher-details">
                          <h4>{teacher.name}</h4>
                          <p>{teacher.specialtySubject} Teacher</p>
                        </div>
                        {selectedTeacherId === teacher.id && (
                          <span className="check-icon">✓</span>
                        )}
                      </div>
                    ))}
                  </div>
                )}
              </div>
              <div className="modal-footer">
                <button className="cancel-button" onClick={() => setShowAssignTeacherModal(false)}>Cancel</button>
                <button 
                  className="confirm-button" 
                  disabled={!selectedTeacherId}
                  onClick={() => handleAssignSubjectTeacher(selectedSubjectForAssignment, selectedTeacherId)}
                >
                  Assign
                </button>
              </div>
            </div>
          </div>
        )}

        {showAddSubjectModal && (
          <div className="modal-overlay">
            <div className="modal-content">
              <div className="modal-header">
                <h3>Add Required Subject</h3>
                <button className="close-button" onClick={() => setShowAddSubjectModal(false)}>×</button>
              </div>
              <div className="modal-body">
                <input
                  type="text"
                  className="subject-input"
                  placeholder="Enter subject name (e.g., Math, Science, Art)"
                  value={newSubject}
                  onChange={(e) => setNewSubject(e.target.value)}
                />
              </div>
              <div className="modal-footer">
                <button className="cancel-button" onClick={() => setShowAddSubjectModal(false)}>Cancel</button>
                <button
                  className="confirm-button"
                  disabled={!newSubject.trim()}
                  onClick={handleAddSubject}
                >
                  Add Subject
                </button>
              </div>
            </div>
          </div>
        )}

        {showAddStudentModal && (
          <div className="modal-overlay">
            <div className="modal-content">
              <div className="modal-header">
                <h3>{editingStudent ? 'Edit Student' : 'Add Student'}</h3>
                <button className="close-button" onClick={() => setShowAddStudentModal(false)}>×</button>
              </div>
              <div className="modal-body">
                <div className="form-group">
                  <label>First Name *</label>
                  <input
                    type="text"
                    value={studentForm.firstName}
                    onChange={(e) => setStudentForm({ ...studentForm, firstName: e.target.value })}
                    placeholder="Enter first name"
                  />
                </div>
                <div className="form-group">
                  <label>Last Name *</label>
                  <input
                    type="text"
                    value={studentForm.lastName}
                    onChange={(e) => setStudentForm({ ...studentForm, lastName: e.target.value })}
                    placeholder="Enter last name"
                  />
                </div>
                <div className="form-group">
                  <label>Student ID {editingStudent ? '(read-only)' : '(optional - auto-generated)'}</label>
                  <input
                    type="text"
                    value={studentForm.studentId}
                    onChange={(e) => setStudentForm({ ...studentForm, studentId: e.target.value })}
                    placeholder="Leave blank to auto-generate"
                    disabled={!!editingStudent}
                  />
                </div>
                <div className="form-group">
                  <label>Email (optional)</label>
                  <input
                    type="email"
                    value={studentForm.email}
                    onChange={(e) => setStudentForm({ ...studentForm, email: e.target.value })}
                    placeholder="student@school.edu"
                  />
                </div>
              </div>
              <div className="modal-footer">
                <button className="cancel-button" onClick={() => setShowAddStudentModal(false)}>Cancel</button>
                <button
                  className="confirm-button"
                  disabled={savingStudent || !studentForm.firstName.trim() || !studentForm.lastName.trim()}
                  onClick={handleSaveStudent}
                >
                  {savingStudent ? 'Saving...' : (editingStudent ? 'Update Student' : 'Add Student')}
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
    );
  }

  return (
    <div className="teacher-classes-view">
      <h2>My Classes</h2>
      <div className="classes-grid">
        {classes.map(cls => (
          <div
            key={cls.id}
            className="class-card"
            onClick={() => handleClassClick(cls)}
          >
            <div className="class-icon">🏫</div>
            <div className="class-details">
              <h3>{cls.name}</h3>
              <p>{cls.grade}</p>
            </div>
            <div className="class-arrow">→</div>
          </div>
        ))}
      </div>
    </div>
  );
}

// Full-screen student detail page — mirrors iOS StudentDetailView
// (photo/header, basic info, class info, academic info, behavior history, recent grades)
function TeacherStudentDetailView({ student, cls, assignments, onBack, onEdit }) {
  const [behaviors, setBehaviors] = useState([]);
  const [grades, setGrades] = useState([]);
  const [attendanceSummary, setAttendanceSummary] = useState({ present: 0, total: 0 });
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let cancelled = false;

    const loadDetails = async () => {
      console.log('🔥 StudentDetail: Loading details for', student.fullName, student.id, student.studentId);
      setLoading(true);
      const idCandidates = [student.id, student.studentId].filter(Boolean);

      // --- Behavior history from studentBehaviors ---
      const behaviorList = [];
      const seenBehaviors = new Set();
      for (const idVal of idCandidates) {
        for (const field of ['studentId', 'studentDocumentId']) {
          try {
            const snap = await getDocs(query(collection(db, 'studentBehaviors'), where(field, '==', idVal)));
            snap.docs.forEach(d => {
              if (!seenBehaviors.has(d.id)) {
                seenBehaviors.add(d.id);
                behaviorList.push({ id: d.id, ...d.data() });
              }
            });
          } catch (e) {
            console.log('🔥 StudentDetail: behavior query failed for', field, idVal, e);
          }
        }
      }
      const toTime = (v) => {
        if (!v) return 0;
        const d = v.toDate ? v.toDate() : new Date(v);
        return isNaN(d.getTime()) ? 0 : d.getTime();
      };
      behaviorList.sort((a, b) => toTime(b.date) - toTime(a.date));
      if (!cancelled) setBehaviors(behaviorList);
      console.log('🔥 StudentDetail: Behaviors loaded', behaviorList.length);

      // --- Recent grades from this class's assignments ---
      const gradeList = [];
      for (const assignment of (assignments || [])) {
        try {
          const gradesSnap = await getDocs(
            collection(db, 'classes', cls.id, 'assignments', assignment.id, 'grades')
          );
          gradesSnap.docs.forEach(d => {
            const g = d.data();
            if (idCandidates.includes(g.studentId)) {
              gradeList.push({ id: d.id, assignmentId: assignment.id, assignmentTitle: assignment.title, ...g });
            }
          });
        } catch (e) {
          console.log('🔥 StudentDetail: grades query failed for assignment', assignment.id, e);
        }
      }
      gradeList.sort((a, b) => toTime(b.gradedAt || b.createdAt) - toTime(a.gradedAt || a.createdAt));
      if (!cancelled) setGrades(gradeList);
      console.log('🔥 StudentDetail: Grades loaded', gradeList.length);

      // --- Attendance summary ---
      try {
        const attSnap = await getDocs(collection(db, 'classes', cls.id, 'attendanceRecords'));
        let present = 0, total = 0;
        attSnap.docs.forEach(d => {
          const r = d.data();
          if (idCandidates.includes(r.studentId)) {
            total++;
            const s = (r.status || '').toLowerCase();
            if (s === 'present' || s === 'excused') present++;
          }
        });
        if (!cancelled) setAttendanceSummary({ present, total });
        console.log('🔥 StudentDetail: Attendance', present, '/', total);
      } catch (e) {
        console.log('🔥 StudentDetail: attendance query failed', e);
      }

      if (!cancelled) setLoading(false);
    };

    loadDetails();
    return () => { cancelled = true; };
  }, [student, cls, assignments]);

  const fmtDate = (v) => {
    if (!v) return 'N/A';
    const d = v.toDate ? v.toDate() : new Date(v);
    return isNaN(d.getTime()) ? 'N/A' : d.toLocaleDateString();
  };

  const averageGrade = grades.length > 0
    ? (grades.reduce((sum, g) => sum + (g.maxScore ? (g.score / g.maxScore) * 100 : 0), 0) / grades.length).toFixed(1) + '%'
    : 'No grades';
  const attendanceRate = attendanceSummary.total > 0
    ? ((attendanceSummary.present / attendanceSummary.total) * 100).toFixed(1) + '%'
    : 'No records';

  return (
    <div className="detail-page">
      <button className="detail-back-button" onClick={onBack}>
        ◄ Back to {cls?.name || 'Students'}
      </button>

      <div className="detail-hero">
        <div className="detail-avatar">
          {student.photoURL ? <img src={student.photoURL} alt={student.fullName} /> : '👤'}
        </div>
        <div className="detail-hero-info">
          <h2>{student.fullName || `${student.firstName || ''} ${student.lastName || ''}`.trim() || 'Student'}</h2>
          <p className="detail-subtitle">Student ID: {student.studentId || 'N/A'}</p>
          <div className="detail-badges">
            {cls?.name && <span className="detail-badge">{cls.name}</span>}
            {cls?.grade && <span className="detail-badge gray">{cls.grade}</span>}
            <span className={`detail-badge ${student.isActive === false ? 'red' : 'green'}`}>
              {student.isActive === false ? 'Inactive' : 'Active'}
            </span>
          </div>
        </div>
        {onEdit && (
          <div className="detail-hero-actions">
            <button className="detail-btn detail-btn-edit" onClick={() => onEdit(student)}>✏️ Edit Student</button>
          </div>
        )}
      </div>

      <div className="detail-grid">
        <div className="detail-card">
          <h3>Basic Information</h3>
          <div className="detail-field"><span className="field-label">First Name</span><span className="field-value">{student.firstName || 'N/A'}</span></div>
          <div className="detail-field"><span className="field-label">Last Name</span><span className="field-value">{student.lastName || 'N/A'}</span></div>
          <div className="detail-field"><span className="field-label">Student ID</span><span className="field-value">{student.studentId || 'N/A'}</span></div>
          {student.email && <div className="detail-field"><span className="field-label">Email</span><span className="field-value">{student.email}</span></div>}
          {student.birthDate && <div className="detail-field"><span className="field-label">Birth Date</span><span className="field-value">{fmtDate(student.birthDate)}</span></div>}
          <div className="detail-field"><span className="field-label">Status</span><span className="field-value">{student.isActive === false ? 'Inactive' : 'Active'}</span></div>
        </div>

        <div className="detail-card">
          <h3>Class Information</h3>
          <div className="detail-field"><span className="field-label">Class Name</span><span className="field-value">{cls?.name || 'N/A'}</span></div>
          <div className="detail-field"><span className="field-label">Grade</span><span className="field-value">{cls?.grade || 'N/A'}</span></div>
          {student.createdAt && <div className="detail-field"><span className="field-label">Joined</span><span className="field-value">{fmtDate(student.createdAt)}</span></div>}
        </div>

        <div className="detail-card">
          <h3>Academic Information</h3>
          <div className="detail-field"><span className="field-label">Attendance Rate</span><span className="field-value">{attendanceRate}</span></div>
          <div className="detail-field"><span className="field-label">Average Grade</span><span className="field-value">{averageGrade}</span></div>
          <div className="detail-field"><span className="field-label">Assignments Graded</span><span className="field-value">{grades.length}/{(assignments || []).length}</span></div>
        </div>

        <div className="detail-card full-width">
          <h3>Recent Grades</h3>
          {loading ? (
            <p className="detail-empty">Loading…</p>
          ) : grades.length === 0 ? (
            <p className="detail-empty">No grades recorded yet</p>
          ) : (
            <div className="detail-list">
              {grades.slice(0, 10).map(g => {
                const pct = g.maxScore ? Math.round((g.score / g.maxScore) * 100) : null;
                return (
                  <div key={g.id} className="detail-list-item">
                    <div>
                      <span className="item-name">{g.assignmentTitle || 'Assignment'}</span>
                      <span className="item-note">{fmtDate(g.gradedAt || g.createdAt)}</span>
                    </div>
                    <span className="item-meta">{g.score}/{g.maxScore}{pct !== null ? ` (${pct}%)` : ''}</span>
                  </div>
                );
              })}
            </div>
          )}
        </div>

        <div className="detail-card full-width">
          <h3>Behavior History</h3>
          {loading ? (
            <p className="detail-empty">Loading…</p>
          ) : behaviors.length === 0 ? (
            <p className="detail-empty">No behavior entries recorded</p>
          ) : (
            <div>
              {behaviors.map(b => (
                <div key={b.id} className="behavior-entry">
                  <div className="behavior-entry-head">
                    <span className={`detail-badge ${b.behaviorType === 'Positive' ? 'green' : 'red'}`}>{b.behaviorType || 'Entry'}</span>
                    <span className="item-name">{b.category || 'General'}</span>
                  </div>
                  {b.note && <p className="behavior-entry-note">{b.note}</p>}
                  <p className="behavior-entry-meta">{fmtDate(b.date)}{b.reportedByName ? ` · ${b.reportedByName}` : ''}{b.reportedByRole ? ` (${b.reportedByRole})` : ''}</p>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

export default TeacherClassesView;
